import React, { useEffect, useMemo, useRef } from "react";

import { useIsMutating, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import ChannelClock from "./ChannelClock";
import EditModeCoolBorder from "./EditModeCoolBorder";
import EditModeSwitch from "./EditModeSwitch";
import SchedulingOverview from "./SchedulingOverview";
import SchedulingTimeline from "./SchedulingTimeline";
import FastAPI from "../../../api/FastAPI";
import MediaAPI from "../../../api/MediaAPI";
import { OFFSET_HOURS } from "../../../constants/fast";
import { DURATION_FILTER_LIST, MEDIA_FORMATS } from "../../../constants/media";
import SCHEDULE_LIST_MUTATION from "../../../constants/mutationKey";
import useFAST from "../../../contexts/FASTContext";
import useGetSchedulesQuery from "../../../hooks/queries/useGetSchedulesQuery";
import useFASTLayout from "../../../hooks/useFASTLayout";
import useTimebounds, {
  TimeboundsProvider,
} from "../../../hooks/useTimebounds";
import useTimelineItems from "../../../hooks/useTimelineItems";
import mergeTimelineData, {
  getIsClustered,
} from "../../../utils/timelineClusters";
import { setTimelineItems } from "../../../utils/timelineItems";
import schedulingTimelineWorkerInstance from "../../../workers/instance";
import Collapsed from "../../common/tools/Collapsed";
import Tooltip from "../../tooltip/Tooltip";
import AbilityContainer from "../../utils/abilities/AbilityContainer";
import MediaLibrary from "../mediaLibrary/MediaLibrary";

import type { JSX } from "react";
import type { TooltipRefProps } from "react-tooltip";

import type { ParametersDataWorker, TimelineContentData } from "../../../types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

function Scheduling(): JSX.Element {
  const { dispatch, state } = useFAST();
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const previewMediaTooltipRef = useRef<TooltipRefProps>(null);
  const workerMessageIdListRef = useRef<string[]>([]);
  const methods = useForm({
    values: {
      isEditMode: false,
    },
  });

  const { isLoadingTimelineItems } = state;

  const isEditMode = methods.watch("isEditMode");

  useFASTLayout({
    title: <span>{t("scheduling")}</span>,
    button: (
      <div className="flex flex-row items-center gap-[0.78125rem]">
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <EditModeSwitch
            isDisabled={isLoadingTimelineItems}
            methods={methods}
          />
        </AbilityContainer>
        <ChannelClock />
      </div>
    ),
  });

  const { getTimebounds, getCurrentDay } = useTimebounds();

  const { startTime: timelineStartDate, endTime: timelineEndDate } =
    getTimebounds();

  const currentDay = getCurrentDay();

  const { currentTimelineMediaList } = useTimelineItems();

  const periodStart = moment(currentDay)
    .startOf("day")
    .subtract(OFFSET_HOURS, "hours")
    .format();
  const periodEnd = moment(currentDay)
    .add(1, "days")
    .startOf("day")
    .add(OFFSET_HOURS, "hours")
    .format();

  const { data: schedules, isLoading: isLoadingSchedules } =
    useGetSchedulesQuery({ channelId });

  const getScheduleAllScheduleListQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleAllScheduleList,
    params: [
      {
        channelId,
        periodEnd,
        periodStart,
      },
    ],
  });

  const {
    data: scheduleAllScheduleList,
    isLoading: isLoadingScheduleAllScheduleList,
  } = useQuery({
    ...getScheduleAllScheduleListQuery,
    enabled: Boolean(channelId),
  });

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: metadataData } = useQuery(getMediasMetadataListQuery);

  const workerRef = useRef<Worker | null>(null);

  const defaultSchedule = useMemo(
    () => schedules?.data?.find((schedule) => schedule.default),
    [schedules?.data],
  );

  const timelinePeriod = timelineEndDate - timelineStartDate;

  const clusteredData = useMemo(
    () =>
      mergeTimelineData({
        data: scheduleAllScheduleList?.data,
        defaultScheduleId: defaultSchedule?.uuid,
        timelinePeriod,
      }),
    [scheduleAllScheduleList?.data, timelinePeriod, defaultSchedule?.uuid],
  );

  const isClustered = getIsClustered(timelinePeriod);

  useEffect(() => {
    const currentWorker = schedulingTimelineWorkerInstance.getWorker();

    workerRef.current = currentWorker;

    currentWorker.onmessage = async (
      event: MessageEvent<TimelineContentData>,
    ): Promise<void> => {
      if (event.data.type !== "scheduling_timeline") {
        return;
      }

      const {
        timeslot,
        variantTimeslots,
        timelineContent,
        mediaIdListOnCurrentDay,
        mediaIdListOnNextDay,
        mediaIdListOnPreviousDay,
        currentDay: currentDayFromWorker,
        isClustered: isClusteredFromWorker,
        previousDay,
        nextDay,
      } = event.data;

      // Les messages pendants sont lus depuis la ref et non depuis la liste figée
      // dans le message : sinon un message posté entre-temps (ex. rollback d'une
      // mise à jour optimiste) n'est jamais marqué comme traité.
      workerMessageIdListRef.current = workerMessageIdListRef.current.slice(1);

      try {
        await setTimelineItems({
          timeslot,
          variantTimeslots,
          timelineContent,
          mediaIdListOnCurrentDay,
          mediaIdListOnNextDay,
          mediaIdListOnPreviousDay,
          currentDay: currentDayFromWorker,
          previousDay,
          nextDay,
          isClustered: isClusteredFromWorker,
        });
      } finally {
        if (workerMessageIdListRef.current.length === 0) {
          dispatch({
            type: "SET_LOADING_TIMELINE_ITEMS",
            isLoading: false,
          });
        }
      }
    };

    return () => {
      currentWorker.onmessage = null;

      if (workerRef.current === currentWorker) {
        workerRef.current = null;
      }

      workerMessageIdListRef.current = [];
      schedulingTimelineWorkerInstance.killWorker();
    };
  }, [dispatch]);

  useEffect(() => {
    const currentWorker = workerRef.current;

    if (clusteredData && schedules?.data && currentWorker) {
      dispatch({
        type: "SET_LOADING_TIMELINE_ITEMS",
        isLoading: true,
      });

      const nextMessageIdList = [...workerMessageIdListRef.current, uuidv4()];

      workerMessageIdListRef.current = nextMessageIdList;

      const message: ParametersDataWorker = {
        scheduleItemList: clusteredData,
        previousCurrentPeriodMediaList: currentTimelineMediaList,
        scheduleList: schedules.data,
        type: "scheduling_timeline",
        currentDay,
        range: {
          start: moment(periodStart).valueOf(),
          end: moment(periodEnd).valueOf(),
        },
        isClustered,
        messageIdList: nextMessageIdList,
      };

      currentWorker.postMessage(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clusteredData,
    schedules,
    workerRef,
    periodStart,
    periodEnd,
    currentDay,
    dispatch,
    isClustered,
  ]);

  const isLoading =
    isLoadingSchedules ||
    isLoadingScheduleAllScheduleList ||
    isLoadingTimelineItems;

  const mutatingScheduleListCount = useIsMutating({
    mutationKey: [SCHEDULE_LIST_MUTATION, channelId],
  });
  const isUpdateDisabled = mutatingScheduleListCount > 0;

  return (
    <TimeboundsProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <EditModeCoolBorder isEditMode={isEditMode} />
        {isEditMode ? (
          <div className="mr-[0.78125rem] flex h-full">
            <Collapsed title={t("programs_inventory")}>
              <MediaLibrary
                isCustomDragLayer
                durationFilterList={DURATION_FILTER_LIST}
                isDraggingDisabled={isUpdateDisabled}
                defaultFormatFilter={{
                  excludes: [MEDIA_FORMATS.ADPOD],
                }}
              />
            </Collapsed>
          </div>
        ) : null}
        <div className="grid w-full grid-flow-row auto-rows-max gap-[0.15625rem]">
          <SchedulingOverview isEditMode={isEditMode} />
          <SchedulingTimeline
            isEditMode={isEditMode}
            isLoading={isLoading}
            isUpdateDisabled={isUpdateDisabled}
            mediaFormatList={metadataData?.formats}
            previewMediaTooltipRef={previewMediaTooltipRef}
          />
          <Tooltip id="timeline-tooltip" />
          <Tooltip ref={previewMediaTooltipRef} imperativeModeOnly />
        </div>
      </FormProvider>
    </TimeboundsProvider>
  );
}

export default Scheduling;
